import { useState } from 'react';
import { BarChart3, PieChart as PieIcon } from 'lucide-react';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PALETTE } from '../lib/constants';
import type { SouscriptionEntry } from '../types';

/**
 * Top compagnies avec toggle [bar horizontal] / [pie donut].
 *
 * Mode bar : top 10, barres horizontales, palette verte décroissante
 * (rank 0 #10b981, rank 1-2 #34d399, rest #a7f3d0).
 *
 * Mode pie : donut 180×180, total au centre, légende top 8 avec %.
 */
export interface TopCompagniesProps {
  entries: SouscriptionEntry[];
  dateField: 'dateSouscription' | 'dateEffet';
}

export function TopCompagnies({ entries }: TopCompagniesProps) {
  const [mode, setMode] = useState<'bar' | 'pie'>('bar');

  const byCompany = aggregateByCompany(entries);

  // Hauteur dynamique du conteneur bar chart : Recharts calcule par défaut
  // la hauteur disponible et peut sauter des labels YAxis quand l'espace
  // est trop serré (> 5 compagnies dans ~260px). On garantit ici ~32 px
  // par ligne (barre 18 + gap). En mode pie la hauteur reste fixe.
  const BAR_ROW_HEIGHT = 32;
  const barRows = Math.min(byCompany.length, 10);
  const minHeight = mode === 'bar' ? Math.max(220, barRows * BAR_ROW_HEIGHT + 40) : 220;

  return (
    // `h-full flex flex-col` : synchronise la hauteur avec SouscriptionsArea
    // via grid items-stretch. Le body en dessous est `flex-1` pour absorber
    // l'espace, les modes bar/pie s'y adaptent via h-full.
    <div className="flex h-full flex-col rounded-stripe border border-stripe-border bg-white p-4 shadow-stripe-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-stripe-text">Top compagnies</div>
          <div className="mt-0.5 text-2xs text-stripe-text-muted">
            Volume de souscriptions par assureur
          </div>
        </div>
        <div className="inline-flex items-center rounded-md border border-stripe-border bg-white text-2xs">
          <button
            type="button"
            onClick={() => setMode('bar')}
            aria-label="Vue barres"
            className={`flex h-6 w-7 items-center justify-center rounded-l-md ${
              mode === 'bar'
                ? 'bg-stripe-accent text-white'
                : 'text-stripe-text-muted hover:text-stripe-text'
            }`}
          >
            <BarChart3 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setMode('pie')}
            aria-label="Vue camembert"
            className={`flex h-6 w-7 items-center justify-center rounded-r-md ${
              mode === 'pie'
                ? 'bg-stripe-accent text-white'
                : 'text-stripe-text-muted hover:text-stripe-text'
            }`}
          >
            <PieIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="mt-4 flex-1" style={{ minHeight }}>
        {mode === 'bar' ? <BarMode rows={byCompany} /> : <PieMode rows={byCompany} />}
      </div>
    </div>
  );
}

function aggregateByCompany(
  entries: SouscriptionEntry[],
): Array<{ company: string; total: number }> {
  const map = new Map<string, number>();
  for (const e of entries) {
    map.set(e.company, (map.get(e.company) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([company, total]) => ({ company, total }))
    .sort((a, b) => b.total - a.total);
}

function BarMode({ rows }: { rows: Array<{ company: string; total: number }> }) {
  const top10 = rows.slice(0, 10);
  return (
    // h-full : le container hérite de la hauteur calculée par la grille
    // parente (alignée avec SouscriptionsArea). Fini la hauteur
    // dynamique `Math.max(200, N*28)` — pas nécessaire puisque la
    // grille impose la taille, et ça évite un décalage visuel.
    <div className="h-full w-full">
      <ResponsiveContainer>
        <BarChart
          data={top10}
          layout="vertical"
          margin={{ left: 0, right: 8, top: 0, bottom: 0 }}
          barCategoryGap="20%"
          barSize={18}
        >
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#8898aa' }}
          />
          <YAxis
            type="category"
            dataKey="company"
            width={100}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: '#8898aa' }}
            // `interval={0}` force l'affichage de tous les labels. Sans ça,
            // Recharts compresse les ticks quand l'espace manque (> 5 barres
            // sur ~260px) et certaines compagnies disparaissent de l'axe.
            interval={0}
          />
          <Tooltip
            cursor={{ fill: '#f7fafc' }}
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: '1px solid #e3e8ee',
              boxShadow: '0 2px 5px 0 rgba(0,0,0,.04)',
            }}
          />
          <Bar dataKey="total" radius={[0, 4, 4, 0]}>
            {top10.map((_, i) => (
              <Cell
                key={i}
                fill={
                  i === 0
                    ? PALETTE.topBar.rank0
                    : i <= 2
                      ? PALETTE.topBar.rank1
                      : PALETTE.topBar.rest
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PieMode({ rows }: { rows: Array<{ company: string; total: number }> }) {
  const top8 = rows.slice(0, 8);
  const total = rows.reduce((s, r) => s + r.total, 0);

  return (
    <div className="flex h-full flex-col items-center gap-3 sm:flex-row">
      <div className="relative h-[180px] w-[180px] shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={top8}
              dataKey="total"
              nameKey="company"
              cx="50%"
              cy="50%"
              outerRadius={80}
              innerRadius={52}
              paddingAngle={2}
              strokeWidth={0}
            >
              {top8.map((_, i) => (
                <Cell key={i} fill={PALETTE.pie[i % PALETTE.pie.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid #e3e8ee',
                boxShadow: '0 2px 5px 0 rgba(0,0,0,.04)',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-lg font-semibold text-stripe-text tabular-nums">
            {total.toLocaleString('fr-FR')}
          </div>
          <div className="text-2xs text-stripe-text-muted">total</div>
        </div>
      </div>
      <ul className="flex-1 space-y-1 text-2xs">
        {top8.map((r, i) => (
          <li key={r.company} className="flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: PALETTE.pie[i % PALETTE.pie.length] }}
            />
            <span className="flex-1 truncate text-stripe-text-secondary">{r.company}</span>
            <span className="tabular-nums text-stripe-text-muted">
              {total === 0 ? '0.0' : ((r.total / total) * 100).toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
